import type { SkillData } from '../spacetime/modules/SkillModule';
import { t } from '../i18n';
import { localizeSkillDescription, localizeSkillName } from '../i18n/content';
import { localizeAndColorSkillTag } from './skillTags';
import { appendGreaterBadge, isGreaterSkill } from './skillVariants';

interface RenderSkillsListOptions {
  enableInteraction: boolean;
  onSkillClick?: (skillId: string) => void;
}

export function renderSkillsList(
  root: HTMLElement,
  skills: SkillData[],
  skillBarSkillIds: string[],
  options: RenderSkillsListOptions
): void {
  root.innerHTML = '';
  const list = document.createElement('div');
  list.className = 'skill-list';

  if (skills.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'char-tab-empty';
    empty.textContent = t('character.noSkills');
    root.appendChild(empty);
    return;
  }

  const sortedSkills = [...skills].sort((left, right) => {
    const leftName = localizeSkillName(left.skillId, left.displayName);
    const rightName = localizeSkillName(right.skillId, right.displayName);
    return leftName.localeCompare(rightName, undefined, { sensitivity: 'base' });
  });

  for (const skill of sortedSkills) {
    const isInBar = skillBarSkillIds.includes(skill.skillId);

    const row = document.createElement('div');
    row.className = 'skill-list-item';
    if (isInBar) row.classList.add('in-bar');

    const iconBox = document.createElement('div');
    iconBox.className = 'skill-icon-box';
    iconBox.style.position = 'relative';
    iconBox.textContent = skill.emoji;

    if (isGreaterSkill(skill.skillId)) {
      appendGreaterBadge(iconBox);
    }

    if (options.enableInteraction && !isInBar) {
      iconBox.draggable = true;
      iconBox.title = t('character.dragToBar');
      iconBox.addEventListener('dragstart', (e) => {
        e.dataTransfer!.setData('application/skill-id', skill.skillId);
        e.dataTransfer!.effectAllowed = 'copy';
        iconBox.classList.add('dragging');
      });
      iconBox.addEventListener('dragend', () => {
        iconBox.classList.remove('dragging');
      });
      if (options.onSkillClick) {
        row.style.cursor = 'pointer';
        row.addEventListener('click', () => {
          options.onSkillClick!(skill.skillId);
        });
      }
    }

    const infoEl = document.createElement('div');
    infoEl.className = 'skill-list-info';

    const nameEl = document.createElement('div');
    nameEl.className = 'skill-list-name';
    const localizedName = localizeSkillName(skill.skillId, skill.displayName);
    const localizedDescription = localizeSkillDescription(skill.skillId, skill.description);

    nameEl.textContent = localizedName;
    if (isInBar) {
      const badge = document.createElement('span');
      badge.className = 'skill-in-bar-badge';
      badge.textContent = t('character.inBar');
      nameEl.appendChild(badge);
    }

    const descEl = document.createElement('div');
    descEl.className = 'skill-list-desc';
    descEl.textContent = localizedDescription;

    const statsEl = document.createElement('div');
    statsEl.className = 'skill-list-stats';
    const stats: string[] = [];
    if (skill.damage > 0) stats.push(t('character.statDmg', { value: skill.damage }));
    if (skill.range > 0) stats.push(t('character.statRange', { value: skill.range }));
    if (skill.durationTicks > 0) stats.push(t('character.statDuration', { value: skill.durationTicks }));
    if (skill.cooldownTicks > 0) stats.push(t('character.statCooldown', { value: skill.cooldownTicks }));
    if (skill.staminaCost > 0) stats.push(`Stamina ${skill.staminaCost}`);
    statsEl.textContent = stats.join(' · ');

    const tagsEl = document.createElement('div');
    tagsEl.className = 'skill-list-tags';
    if (skill.tags.length > 0) {
      skill.tags.forEach((tag, index) => {
        if (index > 0) {
          const separator = document.createElement('span');
          separator.textContent = ', ';
          tagsEl.appendChild(separator);
        }

        const tagEl = document.createElement('span');
        const localizedTag = localizeAndColorSkillTag(tag);
        tagEl.textContent = localizedTag.text;
        tagEl.style.color = localizedTag.color ?? '';
        tagsEl.appendChild(tagEl);
      });
    }

    infoEl.appendChild(nameEl);
    infoEl.appendChild(descEl);
    infoEl.appendChild(statsEl);
    if (skill.tags.length > 0) infoEl.appendChild(tagsEl);
    row.appendChild(iconBox);
    row.appendChild(infoEl);

    list.appendChild(row);
  }

  root.appendChild(list);
}
